/* Account card on the My Trust page — every user sees their two sign-in
   doors (email + phone). Phone-only users can link an email (magic link);
   email-only users can link a phone (SMS OTP). */
import { useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Phone, Mail, CheckCircle2, Loader2, Link2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { useAuth } from "../contexts/AuthContext";
import logging from "../utils/logging";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const detail = (err, fallback) => {
  const d = err.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
};

const LinkedBadge = ({ label }) => (
  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm px-1.5 py-0.5">
    <CheckCircle2 className="w-3 h-3" /> {label}
  </span>
);

function EmailLinkForm({ t }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sentTo, setSentTo] = useState("");
  const [error, setError] = useState("");

  const sendLink = async (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes("@")) { setError(t("account.invalid_email")); return; }
    setBusy(true);
    setError("");
    try {
      await axios.post(`${API}/auth/email/link-request`, { email: email.trim().toLowerCase() }, { withCredentials: true });
      setSentTo(email.trim().toLowerCase());
    } catch (err) {
      logging.error("Email link request failed", err);
      setError(detail(err, t("account.send_failed")));
    } finally {
      setBusy(false);
    }
  };

  if (sentTo) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 flex gap-2" data-testid="account-link-sent">
        <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
        <p className="text-xs text-emerald-900 font-body leading-snug">{t("account.sent_body", { email: sentTo })}</p>
      </div>
    );
  }
  return (
    <form onSubmit={sendLink} noValidate>
      <p className="text-xs text-gray-500 font-body mb-2">{t("account.add_email_hint")}</p>
      <div className="flex gap-2">
        <Input
          type="email" autoComplete="email" value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t("account.email_placeholder")}
          className="rounded-sm h-10 text-sm" data-testid="account-email-input"
        />
        <Button type="submit" disabled={busy} data-testid="account-link-email-btn"
          className="bg-black hover:bg-gray-800 text-white rounded-sm h-10 font-body font-semibold text-xs shrink-0">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Link2 className="w-3.5 h-3.5 mr-1.5" /> {t("account.send_link")}</>}
        </Button>
      </div>
      {error && <p className="text-xs text-red-600 font-body mt-1.5" data-testid="account-link-error">{error}</p>}
    </form>
  );
}

function PhoneLinkForm({ t, onLinked }) {
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState("phone");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const sendCode = async (e) => {
    e?.preventDefault();
    if (!phone.trim()) { setError(t("login.invalid_phone")); return; }
    setBusy(true);
    setError("");
    try {
      await axios.post(`${API}/auth/phone/link-request`, { phone: phone.trim() }, { withCredentials: true });
      setStep("code");
    } catch (err) {
      logging.error("Phone link request failed", err);
      setError(detail(err, t("login.code_send_failed")));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    setError("");
    try {
      const r = await axios.post(`${API}/auth/phone/link-verify`, { phone: phone.trim(), code: code.trim() }, { withCredentials: true });
      toast.success(t("account.phone_linked"));
      onLinked(r.data);
    } catch (err) {
      logging.error("Phone link verify failed", err);
      setError(detail(err, t("login.code_failed")));
    } finally {
      setBusy(false);
    }
  };

  if (step === "code") {
    return (
      <form onSubmit={verify} noValidate data-testid="account-phone-code-step">
        <p className="text-xs text-gray-500 font-body mb-2">
          {t("login.code_sent_body")} <span className="font-mono font-bold text-gray-900">{phone}</span>
        </p>
        <div className="flex gap-2">
          <Input
            type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={8} autoFocus
            value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            placeholder={t("login.code_placeholder")}
            className="rounded-sm h-10 text-sm tracking-[0.3em] font-mono" data-testid="account-phone-code-input"
          />
          <Button type="submit" disabled={busy || code.length < 4} data-testid="account-phone-verify-btn"
            className="bg-black hover:bg-gray-800 text-white rounded-sm h-10 font-body font-semibold text-xs shrink-0">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t("login.verify_code")}
          </Button>
        </div>
        <div className="flex items-center gap-3 mt-1.5 text-[11px] font-body">
          <button type="button" className="text-gray-500 underline" data-testid="account-phone-change"
            onClick={() => { setStep("phone"); setCode(""); setError(""); }}>
            {t("login.different_phone")}
          </button>
          <button type="button" className="text-gray-500 underline disabled:opacity-50" disabled={busy}
            onClick={sendCode} data-testid="account-phone-resend">
            {t("login.resend_code")}
          </button>
        </div>
        {error && <p className="text-xs text-red-600 font-body mt-1.5" data-testid="account-phone-error">{error}</p>}
      </form>
    );
  }
  return (
    <form onSubmit={sendCode} noValidate data-testid="account-phone-input-step">
      <p className="text-xs text-gray-500 font-body mb-2">{t("account.add_phone_hint")}</p>
      <div className="flex gap-2">
        <Input
          type="tel" autoComplete="tel" value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder={t("login.phone_placeholder")}
          className="rounded-sm h-10 text-sm" data-testid="account-phone-input"
        />
        <Button type="submit" disabled={busy} data-testid="account-link-phone-btn"
          className="bg-black hover:bg-gray-800 text-white rounded-sm h-10 font-body font-semibold text-xs shrink-0">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Link2 className="w-3.5 h-3.5 mr-1.5" /> {t("account.link_phone")}</>}
        </Button>
      </div>
      {error && <p className="text-xs text-red-600 font-body mt-1.5" data-testid="account-phone-error">{error}</p>}
    </form>
  );
}

export const LinkEmailCard = () => {
  const { t } = useTranslation();
  const { user, setUser } = useAuth();
  if (!user) return null;

  return (
    <div className="bg-white border border-gray-200 rounded-sm p-5 mb-4" data-testid="account-card">
      <p className="text-xs uppercase tracking-[0.2em] font-bold text-gray-500 mb-3">{t("account.title")}</p>

      <div className="flex items-center gap-2 text-sm font-body mb-1.5" data-testid="account-email">
        <Mail className="w-4 h-4 text-gray-400" />
        {user.email ? (
          <><span className="text-gray-800">{user.email}</span><LinkedBadge label={t("account.linked")} /></>
        ) : (
          <span className="text-gray-400">{t("account.no_email")}</span>
        )}
      </div>
      <div className="flex items-center gap-2 text-sm font-body mb-3" data-testid="account-phone">
        <Phone className="w-4 h-4 text-gray-400" />
        {user.phone ? (
          <><span className="font-mono text-gray-800">{user.phone}</span><LinkedBadge label={t("account.linked")} /></>
        ) : (
          <span className="text-gray-400">{t("account.no_phone")}</span>
        )}
      </div>

      {!user.email && <EmailLinkForm t={t} />}
      {!user.phone && <PhoneLinkForm t={t} onLinked={setUser} />}
    </div>
  );
};

export default LinkEmailCard;
